/**
 * Global Search Component
 * 
 * Provides global search functionality across all entities (equipment, inspections, users).
 * Displays categorized results in a dropdown with keyboard navigation support.
 * 
 * Validates: Requirements 11.1, 11.2
 */

import React, { useState, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
  Paper,
} from '@mui/material';
import {
  Search as SearchIcon,
  Engineering as EquipmentIcon,
  Assignment as InspectionIcon,
  Person as UserIcon,
  Build as MaintenanceIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '../../../hooks/useDebounce';
import { apiService } from '../../../services/api';

// Search result types
export type SearchCategory = 'equipment' | 'inspections' | 'users' | 'maintenance' | 'inventory';

export interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  category: SearchCategory;
  path: string;
}

export interface SearchResponse {
  success: boolean;
  data: {
    equipment: Array<{ id: string; name: string; serialNumber?: string; status?: string }>;
    inspections: Array<{ id: string; title?: string; equipmentName?: string; status?: string }>;
    users: Array<{ id: string; name: string; email?: string; role?: string }>;
    maintenance: Array<{ id: string; title?: string; equipmentName?: string; status?: string }>;
    inventory: Array<{ id: string; name: string; sku?: string; quantity?: number }>;
  };
}

/**
 * Get icon for search result category
 */
function getCategoryIcon(category: SearchCategory): React.ReactNode {
  switch (category) {
    case 'equipment':
      return <EquipmentIcon />;
    case 'inspections':
      return <InspectionIcon />;
    case 'users':
      return <UserIcon />;
    case 'maintenance':
      return <MaintenanceIcon />;
    case 'inventory':
      return <InventoryIcon />;
    default:
      return <SearchIcon />;
  }
}

/**
 * Get display label for category
 */
function getCategoryLabel(category: SearchCategory): string {
  switch (category) {
    case 'equipment':
      return 'Equipment';
    case 'inspections':
      return 'Inspections';
    case 'users':
      return 'Users';
    case 'maintenance':
      return 'Maintenance';
    case 'inventory':
      return 'Inventory';
    default:
      return category;
  }
}

/**
 * Transform API response to search results
 */
function transformSearchResults(response: SearchResponse): SearchResult[] {
  const results: SearchResult[] = [];

  // Transform equipment results
  if (response.data.equipment) {
    response.data.equipment.forEach((item) => {
      results.push({
        id: item.id,
        title: item.name,
        subtitle: item.serialNumber ? `Serial: ${item.serialNumber}` : item.status,
        category: 'equipment',
        path: `/equipment/${item.id}`,
      });
    });
  }

  // Transform inspection results
  if (response.data.inspections) {
    response.data.inspections.forEach((item) => {
      results.push({
        id: item.id,
        title: item.title || `Inspection #${item.id}`,
        subtitle: item.equipmentName || item.status,
        category: 'inspections',
        path: `/inspections/${item.id}`,
      });
    });
  }

  // Transform user results
  if (response.data.users) {
    response.data.users.forEach((item) => {
      results.push({
        id: item.id,
        title: item.name,
        subtitle: item.email || item.role,
        category: 'users',
        path: `/users/${item.id}`,
      });
    });
  }

  // Transform maintenance results
  if (response.data.maintenance) {
    response.data.maintenance.forEach((item) => {
      results.push({
        id: item.id,
        title: item.title || `Maintenance #${item.id}`,
        subtitle: item.equipmentName || item.status,
        category: 'maintenance',
        path: `/maintenance/${item.id}`,
      });
    });
  }

  // Transform inventory results
  if (response.data.inventory) {
    response.data.inventory.forEach((item) => {
      results.push({
        id: item.id,
        title: item.name,
        subtitle: item.sku ? `SKU: ${item.sku}` : `Qty: ${item.quantity}`,
        category: 'inventory',
        path: `/inventory/${item.id}`,
      });
    });
  }

  return results;
}

/**
 * Perform global search across all entities
 */
async function performGlobalSearch(query: string): Promise<SearchResult[]> {
  try {
    const response = await apiService.get<SearchResponse>('/search/global', {
      params: { q: query, limit: 20 },
    });

    if (response.success) {
      return transformSearchResults(response);
    }

    return [];
  } catch (error) {
    console.error('Global search error:', error);
    return [];
  }
}

export interface GlobalSearchProps {
  placeholder?: string;
  minSearchLength?: number;
  debounceMs?: number;
}

/**
 * Global Search Component
 * 
 * Provides search across all entities with categorized results
 */
export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  placeholder = 'Search equipment, inspections, users...',
  minSearchLength = 2,
  debounceMs = 300,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const debouncedQuery = useDebounce(query, debounceMs);

  // Fetch search results
  const { data: results = [], isLoading } = useQuery({
    queryKey: ['globalSearch', debouncedQuery],
    queryFn: () => performGlobalSearch(debouncedQuery),
    enabled: debouncedQuery.length >= minSearchLength,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Handle result selection
  const handleSelect = useCallback(
    (_event: React.SyntheticEvent, value: SearchResult | null) => {
      if (value) {
        navigate(value.path);
        setQuery('');
        setOpen(false);
      }
    },
    [navigate]
  );

  // Handle input change
  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, value: string) => {
      setQuery(value);
      setOpen(value.length >= minSearchLength);
    },
    [minSearchLength]
  );

  return (
    <Autocomplete
      freeSolo
      open={open && query.length >= minSearchLength}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      inputValue={query}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      options={results}
      groupBy={(option) => getCategoryLabel(option.category)}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
      loading={isLoading}
      filterOptions={(x) => x} // Disable client-side filtering (server handles it)
      sx={{
        width: { xs: '100%', sm: 300, md: 400 },
        '& .MuiAutocomplete-inputRoot': {
          paddingRight: '9px !important',
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: 'white',
              },
              '&.Mui-focused': {
                backgroundColor: 'white',
              },
            },
            '& .MuiInputBase-input': {
              color: '#000000 !important',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(0, 0, 0, 0.6) !important',
              opacity: 1,
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#000000' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} sx={{ color: '#000000' }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            'aria-label': 'Global search',
            'aria-describedby': 'global-search-description',
          }}
        />
      )}
      renderOption={(props, option) => (
        <ListItem {...props} key={`${option.category}-${option.id}`}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            {getCategoryIcon(option.category)}
          </ListItemIcon>
          <ListItemText
            primary={option.title}
            secondary={option.subtitle}
            primaryTypographyProps={{
              variant: 'body2',
              noWrap: true,
            }}
            secondaryTypographyProps={{
              variant: 'caption',
              noWrap: true,
            }}
          />
        </ListItem>
      )}
      renderGroup={(params) => (
        <Box key={params.key}>
          <Typography
            variant="caption"
            sx={{
              px: 2,
              py: 1,
              fontWeight: 600,
              color: 'text.secondary',
              backgroundColor: 'action.hover',
              display: 'block',
            }}
          >
            {params.group}
          </Typography>
          {params.children}
        </Box>
      )}
      noOptionsText={
        query.length < minSearchLength
          ? `Type at least ${minSearchLength} characters to search`
          : 'No results found'
      }
      PaperComponent={(props) => (
        <Paper {...props} elevation={8}>
          {props.children}
          <Box
            id="global-search-description"
            sx={{ display: 'none' }}
            role="status"
            aria-live="polite"
          >
            {isLoading
              ? 'Loading search results'
              : `${results.length} results found`}
          </Box>
        </Paper>
      )}
    />
  );
};

export default GlobalSearch;
